import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "../comment-monitoring/comment-monitoring.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import axios from "axios";

const CommentMonitoring = () => {
  const [currentLink, setCurrentLink] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [columns, setColumns] = useState(() => {
    const savedColumns = localStorage.getItem("commentMonitoringColumns");
    return savedColumns ? JSON.parse(savedColumns) : [];
  });
  
  const [loading, setLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [loadingPercentage, setLoadingPercentage] = useState(0); // For percentage tracking
  const [logs, setLogs] = useState(""); // For logs to show beneath the logo
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  // Use useEffect to get the clipboard content when the page loads
  useEffect(() => {
    const fetchClipboardContent = async () => {
      try {
        const clipboardText = await navigator.clipboard.readText();
        setCurrentLink(clipboardText);  // Set clipboard content as the value of currentLink
      } catch (error) {
        console.error("Error reading clipboard content:", error);
      }
    };

    fetchClipboardContent();  // Fetch clipboard content when the component mounts
  }, []);  // Empty dependency array ensures this runs only once on mount
  

  const handleAction = async () => {
    // Check if search keyword is empty
    // if (!searchKeyword.trim()) {
    //   setTimeout(() => toast.error("Please enter a keyword to search."), 0); // 🔹 Ensures execution
    //   return; 
    // }
    

    console.log("Action initiated.");

    if (!currentLink.trim()) {
      toast.error("Please enter a valid link.");
      console.log("No link provided.");
      return;
    }

    const platform = detectPlatform(currentLink.trim());
    setDetectedPlatform(platform);

    if (!platform) {
      toast.error(
        "Unsupported or invalid link. Please enter a valid social media link."
      );
      console.log("Platform detection failed.");
      return;
    }

    console.log(`Detected platform: ${platform}`);
    setLoading(true);
    setLogs("Fetching post data...");
    setLoadingPercentage(10); // Start loading percentage

    const { postId, username } = extractPostData(currentLink.trim(), platform);

    if (!postId) {
      toast.error(`Invalid post link format for ${platform}.`);
      console.log("Post ID extraction failed.");
      setLoading(false);
      return;
    }

    console.log(`Extracted Post ID: ${postId}, Username: ${username}`);

    const existingColumn = columns.find((col) => col.title === postId);
    if (existingColumn) {
      toast.error(`Column with Post ID ${postId} already exists.`);
      setLoading(false);
      return;
    }

    try {
      const response = await fetchComments(platform, postId, searchKeyword); // Fetch comments based on the search term
      setLoadingPercentage(50);

      if (response.status === 200 && response.data.length > 0) {
        console.log(`Comments found for ${platform}, displaying results...`);
        setLogs("Comments fetched, displaying results...");
        updateColumns(platform, postId, response.data, searchKeyword); // Pass search keyword to updateColumns
      } else {
        console.log(
          `No comments found in DB for ${platform}. Fetching from external source...`
        );
        setLogs(
          "No comments found in database, fetching from external sources..."
        );
        await addLink(postId, platform, username);
      }
    } catch (error) {
      setLogs("Error occurred while fetching comments.");
      toast.error(`Error fetching comments for ${platform}.`);
    } finally {
      setLoading(false);
      setLoadingPercentage(100); // Final loading step
      setIsFormCollapsed(true); // Collapse the form after search initiation
    } 
  };

  const fetchComments = async (platform, postId, searchKeyword) => {
    const fetchEndpoint = `api/fetch/${platform.toLowerCase()}-comment/`;
    try {
      const response = await axios.post(fetchEndpoint, {
        post_id: postId,
        search_term: searchKeyword, // Include the search keyword
      });
      return response; // Return response for further handling
    } catch (error) {
      console.error(`Error fetching comments for ${platform}:`, error);
      throw error; // Rethrow the error to be handled in the calling function
    }
  };

  const handleColumnSearch = (columnId, searchTerm) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId
          ? {
              ...col,
              searchTerm, // Update search term for the column
            }
          : col
      )
    );
  };

  const updateColumns = (platform, postId, responseData, primarySearchTerm) => {
    setColumns((prevColumns) => {
      const platformCount = prevColumns.filter(col => col.platform === platform).length;
      const newColumnId = `${platform.toLowerCase()}-column-${platformCount}`;
  
      const updatedColumns = [
        ...prevColumns,
        {
          id: newColumnId,
          platform,
          posts: Array.isArray(responseData) ? responseData : [],
          title: postId,
          fullPostLink: currentLink.trim(),
          primarySearchTerm,
          columnSearchTerm: "",
        },
      ];
  
      localStorage.setItem("commentMonitoringColumns", JSON.stringify(updatedColumns)); // ✅ Save to localStorage
  
      return updatedColumns;
    });
  };
  
  

  const detectPlatform = (input) => {
    console.log(`Detecting platform for input: ${input}`);
    const trimmedInput = input.trim();
  
    // ✅ Check if input is only numeric → Assume it's a Facebook post ID
    if (/^\d+$/.test(trimmedInput)) {
      return "Facebook";
    }
  
    // ✅ Detect platform from URLs
    if (trimmedInput.includes("facebook.com")) return "Facebook";
    if (trimmedInput.includes("instagram.com")) return "Instagram";
    if (trimmedInput.includes("tiktok.com")) return "TikTok";
  
    return null; // No match
  };

  const extractPostData = (link, platform) => {
    try {
      let postId = null,
        username = null;

      switch (platform.toLowerCase()) {
        case "facebook":
          // ✅ Extract post ID from a URL like "https://www.facebook.com/1032759675559800"
          let match = link.match(/https:\/\/www\.facebook\.com\/(\d+)/);
          postId = match ? match[1] : null;  // Extract the numeric postId
          break;
      

        case "instagram":
          let instagramMatch = link.match(/instagram\.com\/(?:p|reel)\/([^/]+)/);
          postId = instagramMatch ? instagramMatch[1] : null;
          break;
        

        case "tiktok":
          let tiktokMatch = link.match(/tiktok\.com\/@([^/]+)\/video\/(\d+)/);
          if (tiktokMatch) {
            username = tiktokMatch[1];
            postId = tiktokMatch[2];
          }
          break;

        default:
          return { postId: null, username: null };
      }

      return { postId, username };
    } catch (error) {
      console.error("Error extracting post data:", error);
      return { postId: null, username: null };
    }
  };

  const addLink = async (postId, platform, username) => {
    const endpoint = `api/${platform.toLowerCase()}-comment/`;

    try {
      const requestData = { post_id: postId };

      if (username) {
        requestData.username = username;
      }

      const response = await axios.post(endpoint, requestData);

      if (response.status === 201 || response.status === 200) {
        console.log(
          `Successfully fetched and stored comments for ${platform}.`
        );

        // Call fetchComments to get the updated data
        const fetchResponse = await fetchComments(
          platform,
          postId,
          searchKeyword
        );

        if (fetchResponse.status === 200 && fetchResponse.data.length > 0) {
          console.log(
            `Comments fetched for ${platform}, displaying results...`
          );

          // Call updateColumns to add the new column with fetched data
          updateColumns(platform, postId, fetchResponse.data);
        } else {
          console.log(`No comments found after fetching for ${platform}.`);
          setLogs("No comments found after fetching.");
        }
      } else {
        toast.error("Failed to fetch comments. Please try again.");
      }
    } catch (error) {
      console.error("Error adding link:", error);
      toast.error("Error adding link. Please check the backend.");
    }
  };

  const handleEditTitle = (columnId, newTitle) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId ? { ...col, title: newTitle } : col
      )
    );
  };

  const highlightText = (text, primarySearchTerm, columnSearchTerm) => {
    if (!text) return "";

    try {
      let highlightedText = text;

      // Function to escape special characters in regex
      const escapeRegex = (str) =>
        str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");

      // Highlight Primary Search (Yellow)
      if (primarySearchTerm) {
        const primaryTerms = primarySearchTerm
          .split("+")
          .map((term) => escapeRegex(term.trim()))
          .filter(Boolean);
        if (primaryTerms.length > 0) {
          const primaryRegex = new RegExp(`(${primaryTerms.join("|")})`, "gi");
          highlightedText = highlightedText.replace(
            primaryRegex,
            (match) => `<mark style="background-color: yellow;">${match}</mark>`
          );
        }
      }

      // Highlight Column Search (Green)
      if (columnSearchTerm) {
        const columnTerms = columnSearchTerm
          .split("+")
          .map((term) => escapeRegex(term.trim()))
          .filter(Boolean);
        if (columnTerms.length > 0) {
          const columnRegex = new RegExp(`(${columnTerms.join("|")})`, "gi");
          highlightedText = highlightedText.replace(
            columnRegex,
            (match) =>
              `<mark style="background-color: lightgreen;">${match}</mark>`
          );
        }
      }

      return highlightedText;
    } catch (error) {
      console.error("Error highlighting text:", error);
      return text;
    }
  };

  const handleDeleteColumn = (columnId) => {
    setColumns((prevColumns) => {
      const updatedColumns = prevColumns.filter((col) => col.id !== columnId);
      localStorage.setItem("commentMonitoringColumns", JSON.stringify(updatedColumns)); // ✅ Sync localStorage
      return updatedColumns;
    });
  };
  
  const handleFilterChange = (columnId, type, value) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId ? { ...col, [type]: value } : col
      )
    );
  };

  const handleResetFilter = (columnId) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId ? { ...col, fromDate: null, toDate: null } : col
      )
    );
  };

  return (
    <div className={`comment-monitoring ${loading ? "blurred" : ""}`}>
      <ToastContainer
    position="top-right"
    autoClose={5000}
    hideProgressBar={false}
    newestOnTop={false}
    closeOnClick
    pauseOnFocusLoss
    draggable
    pauseOnHover
  />
      {loading && (
        <div className="loading-overlay">
          {/* Original Spinning Logo */}
          <img src="/icons/logo.png" alt="Loading" className="spinning-logo" />

          {/* New Loading Text and Logs */}
          <div className="loading-text">Loading...</div>
          <div className="loading-logs">
            <div>PROGRESS: {loadingPercentage}%</div>
            <div>{logs}</div>
          </div>
        </div>
      )}

      <button
        className="toggle-button"
        onClick={() => setIsFormCollapsed(!isFormCollapsed)} // Toggle between collapsed and expanded form
      >
        {isFormCollapsed ? "+" : "−"}{" "}
        {/* If the form is collapsed, show "+" to expand, otherwise show "−" */}
      </button>

      {!isFormCollapsed && (
        <div
          className="comment-monitoring-form"
          style={{
            backgroundColor: "white",
            padding: "55px",
            borderRadius: "8px",
          }}
        >
          <h1>Comments Monitoring</h1>
          <div className="comment-monitoring-form-group">
            {/* New Div for Supported Platforms */}
            <div className="comment-monitoring-div">
              <h2>Supported Platforms</h2>
              <div className="supported-platform-icons">
                <FontAwesomeIcon icon={faFacebook} size="2x" color="#4267B2" />
                <FontAwesomeIcon icon={faInstagram} size="2x" color="#C13584" />
                <FontAwesomeIcon icon={faTiktok} size="2x" color="#69C9D0" />
              </div>
            </div>
            <label htmlFor="post-link">Enter Post Link: ⤵️</label>
            <input
              type="text"
              id="post-link"
              placeholder="Enter post link"
              value={currentLink}
              onChange={(e) => {
                setCurrentLink(e.target.value);
                const platform = detectPlatform(e.target.value);
                setDetectedPlatform(platform);
              }}
            />

            {detectedPlatform ? (
              <span className="comment-monitoring-platform-icon">
                {detectedPlatform === "Facebook" && (
                  <FontAwesomeIcon
                    icon={faFacebook}
                    size="lg"
                    color="#4267B2"
                    style={{ marginRight: "5px" }}
                  />
                )}
                {detectedPlatform === "Instagram" && (
                  <FontAwesomeIcon
                    icon={faInstagram}
                    size="lg"
                    color="#C13584"
                    style={{ marginRight: "5px" }}
                  />
                )}
                {detectedPlatform === "TikTok" && (
                  <FontAwesomeIcon
                    icon={faTiktok}
                    size="lg"
                    color="#69C9D0"
                    style={{ marginRight: "5px" }}
                  />
                )}
                <span className="detected-platform-text">
                  Detected Platform: {detectedPlatform}
                </span>
              </span>
            ) : (
              currentLink && (
                <span className="detected-platform-text unsupported">
                  
                </span>
              )
            )}

            <label htmlFor="search-keyword">Search Comments: ⤵️</label>
            <input
              type="text"
              id="search-keyword"
              placeholder="Enter Comment to Search..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)} // Set the form search term
            />

            <button
              className="comment-monitoring-search-button"
              onClick={handleAction}
              disabled={loading} // Disable the button if no search term is entered
            >
              <i className="fa fa-search"></i> {/* FontAwesome icon */}
              START SEARCHING
            </button>
            {/* Toast Container - Place it at the bottom of your component */}
          </div>
        </div>
      )}

      {/* Columns Wrapper */}
      <div className="comment-monitoring-columns-wrapper">
      {columns.map((column) => (
  <div key={column.id} className={`comment-monitoring-column ${column.id}`}>

            <div
              className="comment-monitoring-header-card"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              {/* Platform Icon */}
              {column.platform === "Facebook" && (
                <FontAwesomeIcon icon={faFacebook} size="2x" color="#4267B2" />
              )}
              {column.platform === "Instagram" && (
                <FontAwesomeIcon icon={faInstagram} size="2x" color="#C13584" />
              )}
              {column.platform === "TikTok" && (
                <FontAwesomeIcon icon={faTiktok} size="2x" color="#69C9D0" />
              )}

              {/* Static non-editable column title */}
              {/* Static non-editable full post link with hyperlink */}
              {/* Static non-editable full post link with hyperlink and text wrapping */}
              {/* Static non-editable full post link with hyperlink and inline text wrapping */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                <h3
                  style={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    marginBottom: "10px",
                    color: "#65ccb7",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  POST LINK:
                </h3>
                <a
                  href={column.fullPostLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontWeight: "normal",
                    color: "#007bff",
                    textDecoration: "underline",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    display: "block",
                    maxWidth: "90%",
                  }}
                >
                  {column.fullPostLink}
                </a>
              </div>

              {/* Search input */}
              <label
                htmlFor="search-comments"
                style={{
                  fontWeight: "bold",
                  color: "#65ccb7",
                  marginBottom: "5px",
                }}
              >
                SEARCH COMMENTS: ⤵️
              </label>
              <input
                type="text"
                className="comment-monitoring-header-input"
                value={column.columnSearchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setColumns((prevColumns) =>
                    prevColumns.map((col) =>
                      col.id === column.id
                        ? { ...col, columnSearchTerm: newSearchTerm }
                        : col
                    )
                  );
                }}
                placeholder="Search comments..."
              />

              {/* Delete button */}
              <button
                className="delete-column-button"
                onClick={() => handleDeleteColumn(column.id)}
              >
                <span className="cross-button">X</span>
              </button>

              {/* Date Filters */}
              <div className="comment-monitoring-date-filter">
                <h3>Select Date Range 🕑</h3>
                <div className="date-inputs">
                  <label>From:</label>
                  <input
                    type="date"
                    value={column.fromDate || ""}
                    onChange={(e) =>
                      handleFilterChange(column.id, "fromDate", e.target.value)
                    }
                  />
                  <label>To:</label>
                  <input
                    type="date"
                    value={column.toDate || ""}
                    onChange={(e) =>
                      handleFilterChange(column.id, "toDate", e.target.value)
                    }
                  />
                </div>
                <button
                  className="reset-button"
                  onClick={() => handleResetFilter(column.id)}
                >
                  Reset Date
                  <i
                    className="fa fa-undo"
                    aria-hidden="true"
                    style={{ gap: "80px" }}
                  ></i>
                </button>
              </div>
            </div>

            <div className="comment-monitoring-posts-wrapper">
  {(Array.isArray(column.posts) ? column.posts : [])
    .filter((post) => {
      // Date Filtering Logic
      if (!column.fromDate && !column.toDate) return true;
      const postDate = new Date(post.timestamp * 1000);
      const fromDate = column.fromDate ? new Date(column.fromDate) : null;
      const toDate = column.toDate ? new Date(column.toDate) : null;
      return (
        (!fromDate || postDate >= fromDate) &&
        (!toDate || postDate <= toDate)
      );
    })
    .filter((post) => {
      // Multi-word Search Filtering Logic for column search
      if (!column.columnSearchTerm) return true;  // If no search term is entered, show all posts

      const terms = column.columnSearchTerm
        .split("+") // Split by '+'
        .map((term) => term.trim().toLowerCase()) // Trim spaces and convert to lowercase
        .filter((term) => term.length > 0); // Remove empty terms

      if (terms.length === 0) return true;

      const postText = (post.text || "").toLowerCase();  // Use text or comment_text

      // Ensure all search terms are present in the comment text
      return terms.every((term) => postText.includes(term));
    })
    .map((post, idx) => (
      <div
        key={idx}
        className={`comment-monitoring-postcard ${column.platform.toLowerCase()}`}
      >
        {/* Post Header */}
        <div
          className="comment-monitoring-postcard-header"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div
            className="post-username"
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "normal",
            }}
          >
            <span className="username-text" style={{ fontWeight: "bold" }}>
              @
              {post.owner_username ||
                post.author_username ||
                "Unknown User"}
            </span>
            <span className="timestamp-text" style={{ fontWeight: "bolder" }}>
              {new Date(post.timestamp * 1000).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>

          <div className="fullname" style={{ marginTop: "5px" }}>
            <span style={{ fontWeight: "bold" }}>FULL NAME: </span>
            <span style={{ fontStyle: "italic" }}>
              {post.author_full_name || post.owner_full_name}
            </span>
          </div>
        </div>

        {/* Post Body */}
        <div className="comment-monitoring-postcard-body">
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(
                post.text || post.comment_text || "",
                column.primarySearchTerm,
                column.columnSearchTerm
              ),
            }}
          ></p>

          {post.attached_image_url && (
            <img
              src={post.attached_image_url}
              alt="Attached"
              className="post-image"
            />
          )}
        </div>

        {/* Post Footer */}
        <div className="comment-monitoring-postcard-footer">
          {column.platform === "Facebook" && (
            <>
              <span style={{ marginRight: "10px" }}>
                👍 {post.reactions_like_count || 0}
              </span>
              <span style={{ marginRight: "10px" }}>
                😂 {post.reactions_haha_count || 0}
              </span>
              <span>😢 {post.reactions_sad_count || 0}</span>
            </>
          )}
          {column.platform === "Instagram" && (
            <>
              <span style={{ marginRight: "10px" }}>
                ❤️ {post.like_count || 0}
              </span>
              <span>💬 {post.comments_count || 0}</span>
            </>
          )}
          {column.platform === "TikTok" && (
            <>
              <span style={{ marginRight: "10px" }}>
                ❤️ {post.digg_count || 0}
              </span>
              <span>💬 {post.reply_count || 0}</span>
            </>
          )}
        </div>
      </div>
    ))}
</div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default CommentMonitoring;
